import { ref, watch, computed, onMounted } from "@vue/composition-api";
import store from "@/store";
import { formatCurrency } from "@core/utils/filter";
import router from "@/router";
// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ability from "@/libs/acl/ability";
import { getUserData } from "@/auth/utils";
import { getDateTime } from "@/libs/helper";


export default function useClaimsList() {
  // Use toast
  const toast = useToast();

  const refClaimListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: "claim_number", label: "編號", sortable: true, class: 'sticky-column' },
    { key: "actions", label: "操作", class: 'sticky-column'},
    // { key: "requires_invoice", label: "需要發票" },
    { key: "invoice_info", label: "報銷發票" },
    { key: "submission_date", label: "報銷日期", sortable: true },
    { key: "payment_method", label: "付款方式", sortable: true },
    { key: "purpose", label: "報銷原因" },
    { key: "show_details", label: "報銷詳情" },
    { key: "user.name_cn", label: "報銷者" },
    { key: "total_amount", label: "金額", sortable: true, formatter: formatCurrency },
    { key: "claim_status", label: "報銷狀態" },
    // { key: "is_approved", label: "已批核" },
    // { key: "approve_remark", label: "批核備註" },
    { key: "created_at", label: "創建日期", sortable: true },
  ];
  const perPage = ref(10);
  const totalRow = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("submission_date");
  const isSortDirDesc = ref(true);

  const dataMeta = computed(() => {
    const localItemsCount = refClaimListTable.value
      ? refClaimListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRow.value,
    };
  });

  const refetchData = () => {
    refClaimListTable.value.refresh();
  };

  const claimsList = ref(null);

  // 過濾器狀態
  const filter_invoice_id = ref(null);
  const filter_start_date = ref(null);
  const filter_end_date = ref(null);
  const filter_min_amount = ref(0);
  const filter_max_amount = ref(1000000);

  const filter_user_id = ref(null);
  const filter_claim_status_id = ref(null);

  watch(
    [
      currentPage,
      perPage,
      searchQuery,
      filter_user_id,
      filter_claim_status_id,
      filter_invoice_id,
      filter_start_date,
      filter_end_date,
      filter_min_amount,
      filter_max_amount,
    ],
    () => {
      refetchData();
    }
  );

  const is_user_page = ref(false);
  const fetchClaims = (ctx, callback) => {
    if(router.currentRoute.name  == 'worker-claim-list'){
      var url = 'fetchSelfClaims'
      is_user_page.value = true
    }else{
      var url = 'fetchClaims'
      is_user_page.value = false
    }
    store
      .dispatch(`claims/${url}`, {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,

        user_id: filter_user_id.value,
        claim_status_id: filter_claim_status_id.value,
        invoice_id: filter_invoice_id.value,
        start_date: filter_start_date.value,
        end_date: filter_end_date.value,
        min_amount: filter_min_amount.value,
        max_amount: filter_max_amount.value,
      })
      .then((response) => {
        const { claims, total } = response.data;
        callback(claims);
        totalRow.value = total;
        claimsList.value = claims;
      })
      .catch((err) => {
        console.log(err);
        toast({
          component: ToastificationContent,
          props: {
            title: "獲取報銷清單時出錯",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
          });
        });
  };

  const productCategoriesOptions = ref([]);
  const productOptions = ref([]);
  const claimStatusOptions = ref([]);
  const userOptions = ref([]);
  const invoiceOptions = ref([]);

  const refetchOption = () => {
    store
      .dispatch("app/fetchOptionList", {
        product_categories: true,
        products: true,
        claim_statuses: true,
        users: true,
        invoices: true
      })
      .then((response) => {
        productOptions.value = response.data.products;
        productCategoriesOptions.value = response.data.product_categories;
        claimStatusOptions.value = response.data.claim_statuses;
        userOptions.value = response.data.users;
        invoiceOptions.value = response.data.invoices;
        console.log(claimStatusOptions.value);
      });
  };

  onMounted(() => {
    refetchOption();
  });

  // 匯出記錄的主要函數
  const exportRecords = (exportType) => {
    return new Promise((resolve, reject) => {
      let dataToExport;

      if (exportType === 'current') {
        // 匯出當前頁面數據
        dataToExport = claimsList.value;
        processExportData(dataToExport, resolve);
      } else {
        // 匯出全部數據 - 需要重新獲取所有記錄
        const url = is_user_page.value ? 'fetchSelfClaims' : 'fetchClaims';

        store
          .dispatch(`claim/${url}`, {
            q: searchQuery.value,
            perPage: 999999, // 獲取所有記錄
            page: 1,
            sortBy: sortBy.value,
            sortDesc: isSortDirDesc.value,
            user_id: filter_user_id.value,
            claim_status_id: filter_claim_status_id.value,
            invoice_id: filter_invoice_id.value,
            start_date: filter_start_date.value,
            end_date: filter_end_date.value,
            min_amount: filter_min_amount.value,
            max_amount: filter_max_amount.value,
          })
          .then((response) => {
            const { claims } = response.data;
            processExportData(claims, resolve);
          })
          .catch((error) => {
            console.error('獲取全部記錄失敗:', error);
            reject(error);
          });
      }
    });
  };

  // 處理匯出數據的共用函數
  const processExportData = (dataToExport, resolve) => {
    // 按報銷日期排序數據
    const sortedData = [...dataToExport].sort((a, b) => {
      const dateA = new Date(a.submission_date || a.created_at);
      const dateB = new Date(b.submission_date || b.created_at);
      return dateB - dateA; // 降序排列（最新的在前）
    });

    // 準備匯出數據
    const exportData = [];

    sortedData.forEach((item) => {
      // 處理報銷詳情的格式化
      const claimDetails = formatClaimDetails(item);

      exportData.push({
        '編號': item.claim_number || '',
        '報銷發票': formatInvoiceInfo(item),
        '報銷日期': item.submission_date || '',
        '付款方式': formatPaymentMethod(item),
        '報銷原因': item.purpose || '- -',
        '報銷詳情': claimDetails,
        '報銷者': item.user?.name_cn || '',
        '報銷金額': formatCurrency(item.total_amount),
        '報銷狀態': item.claimStatus?.name || item.claim_status?.name || getClaimStatusName(item.claim_status_id) || '- -',
        '創建日期': getDateTime(item.created_at, true) || ''
      });
    });

    resolve(exportData);
  };

  // 格式化報銷詳情的函數
  const formatClaimDetails = (item) => {
    const details = [];

    // 處理 expense_items 資訊（注意：實際欄位名稱是 expense_items）
    const expenseItems = item.expense_items || item.expenseItems || [];
    if (expenseItems && expenseItems.length > 0) {
      expenseItems.forEach((expenseItem, index) => {
        // 支援兩種分類欄位名稱：purchase_category_id 和 product_category_id
        const categoryId = expenseItem.purchase_category_id || expenseItem.product_category_id;
        const categoryName = productCategoriesOptions.value?.find(cat => cat.id === categoryId)?.name || '未知分類';

        details.push(`代購分類：${categoryName}`);
        details.push(`代購內容：${expenseItem.remark || '- -'}`);
        details.push(`金額：$${Number(expenseItem.total || 0).toFixed(0)}`);

        if (index < expenseItems.length - 1) {
          details.push(''); // 空行分隔不同項目
        }
      });
    }

    // 如果沒有報銷項目，返回預設值
    if (details.length === 0) {
      return '- -';
    }

    return details.join('\n');
  };

  // 輔助函數
  const formatInvoiceInfo = (item) => {
    if (item.invoice) {
      return `[${item.invoice.invoice_number}] ${item.invoice.name_cn || '未指定客戶'}`;
    }
    return '無報銷發票';
  };

  // 格式化付款方式（支援支票號碼換行顯示）
  const formatPaymentMethod = (item) => {
    if (!item.payment_method_id) {
      return '- -';
    }

    const methods = { 1: '現金', 2: '支票', 3: '銀行轉帳' };
    const methodName = methods[item.payment_method_id] || '- -';

    // 如果是支票且有支票號碼，換行顯示
    if (item.payment_method_id === 2 && item.check_number) {
      return `${methodName}\n${item.check_number}`;
    }

    return methodName;
  };

  const getPaymentMethodName = (methodId) => {
    // 這裡需要根據實際的付款方式選項來實作
    const methods = { 1: '現金', 2: '支票', 3: '銀行轉帳' };
    return methods[methodId] || '- -';
  };

  const getClaimStatusName = (statusId) => {
    if (!statusId || !claimStatusOptions.value || !claimStatusOptions.value.length) {
      return '- -';
    }
    const status = claimStatusOptions.value.find(option => option.id === statusId);
    return status ? status.name : '- -';
  };

  const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return {
    fetchClaims,
    tableColumns,
    perPage,
    totalRow,
    currentPage,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    dataMeta,
    claimsList,
    refClaimListTable,
    ability,
    refetchData,
    filter_user_id,
    filter_claim_status_id,
    filter_invoice_id,
    filter_start_date,
    filter_end_date,
    filter_min_amount,
    filter_max_amount,
    userOptions,
    productCategoriesOptions,
    claimStatusOptions,
    productOptions,
    invoiceOptions,
    is_user_page,
    exportRecords,
  };
}
